import { useMemo, useState, useEffect, type MouseEvent } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  MapPin,
  Clock,
  Phone,
  MessageCircle,
  ShieldCheck,
  ArrowRight,
  Share2,
  Heart,
  Flag,
  Info,
  ChevronRight,
  ChevronLeft,
  User,
  Calendar,
  Eye,
  CheckCircle2,
  Fuel,
  Gauge,
  Settings,
  Mail,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useMobileMenu } from '../hooks/useMobileMenu';
import { useAdDetails } from '../hooks/useAds';
import type { Ad } from '../types/ad';
import { useAuth } from '../hooks/AuthContext';
import * as savedService from '../services/savedService';
import AuthModal from '../components/AuthModal';

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

const PLACEHOLDER_IMAGE =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"><rect width="100%" height="100%" fill="%23e5e7eb"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-family="sans-serif" font-size="18">No Image</text></svg>';

const formatPKR = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return String(value);
  return new Intl.NumberFormat('en-PK', {
    style: 'currency',
    currency: 'PKR',
    maximumFractionDigits: 0,
  }).format(num);
};

const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-PK').format(value);
};

const formatRelativeTime = (iso: string | undefined): string => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const diffMs = Date.now() - then;
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.round(months / 12);
  return `${years} year${years === 1 ? '' : 's'} ago`;
};

const titleCase = (value: string | null | undefined): string => {
  if (!value) return '';
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const buildWhatsAppLink = (phone: string, title: string): string => {
  const digits = phone.replace(/\D/g, '');
  const text = encodeURIComponent(`Hi, I'm interested in your listing: ${title}`);
  return `https://wa.me/${digits}?text=${text}`;
};

const getCategoryName = (ad: Ad): string => {
  if (typeof ad.category === 'string') return ad.category_name || 'Category';
  return ad.category?.name || ad.category_name || 'Category';
};

/**
 * Build a human-readable breadcrumb trail for the ad's category.
 *
 * Backend shapes we accept (in priority order):
 *   1. `category.full_path` as `Array<{name: string, ...}>` (detail endpoint)
 *   2. `category.breadcrumb_name` string e.g. "Cars & Vehicles > Cars"
 *   3. `category.full_path` string (legacy)
 *   4. top-level `category_path` string
 */
type CategorySegment = { name: string; slug?: string };

const getCategorySegments = (ad: Ad): CategorySegment[] => {
  const category = typeof ad.category === 'string' ? null : ad.category;

  // 1. Array of segment objects → map to names
  if (category && Array.isArray(category.full_path)) {
    return category.full_path
      .map((seg) => {
        if (seg && typeof seg === 'object') {
          return { name: seg.name as string, slug: (seg as any).slug as string | undefined };
        }
        return { name: String(seg) };
      })
      .filter((s) => Boolean(s && typeof s.name === 'string' && s.name));
  }

  // 2–4. String sources
  const raw =
    (category && typeof category.full_path === 'string' && category.full_path) ||
    (category && category.breadcrumb_name) ||
    ad.category_path ||
    '';

  return String(raw)
    .split(/>|\/|»/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((name) => ({ name }));
};

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

export default function ItemDetailsPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { mobileOpen, toggleMobile } = useMobileMenu();

  const { ad, isLoading, error, refetch } = useAdDetails(id);
  const { isAuthenticated } = useAuth();

  const [activeImageIdx, setActiveImageIdx] = useState<number>(0);
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [phoneRevealed, setPhoneRevealed] = useState<boolean>(false);
  const [sellerImgOk, setSellerImgOk] = useState<boolean>(true);
  const [authOpen, setAuthOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setPhoneRevealed(false);
    }
  }, [isAuthenticated]);

  // Client-side 24h throttle marker (for analytics parity; backend enforces uniqueness)
  useEffect(() => {
    if (!id) return;
    const key = `ad_viewed_${id}`;
    const now = Date.now();
    try {
      const prev = Number(localStorage.getItem(key) || '0');
      // Update stamp if last mark older than 24h
      if (!prev || now - prev > 24 * 60 * 60 * 1000) {
        localStorage.setItem(key, String(now));
      }
    } catch {
      // storage may be unavailable; ignore
    }
  }, [id]);

  const gallery = useMemo<string[]>(() => {
    if (!ad) return [];
    const imgs = Array.isArray(ad.images) ? ad.images.filter(Boolean) : [];
    if (imgs.length > 0) return imgs;
    if (ad.primary_image) return [ad.primary_image];
    return [];
  }, [ad]);

  // Initialize saved state from API when authenticated
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isAuthenticated || !ad) return;
      try {
        const data = await savedService.listSavedItems();
        if (cancelled) return;
        const found = data.items.some((it) => it.ad.id === ad.id);
        setIsSaved(found);
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, ad]);

  // ── Loading state ─────────────────────────────────────────
  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>Loading listing… · BazaarPk</title>
        </Helmet>
        <div className="min-h-screen bg-surface">
          <Navbar mobileOpen={mobileOpen} onToggleMobile={toggleMobile} />
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="grid lg:grid-cols-[1fr_380px] gap-8 items-start">
              <div className="space-y-8">
                <div className="bg-white rounded-3xl border border-dark-200 overflow-hidden shadow-sm">
                  <div className="h-[400px] sm:h-[540px] bg-dark-100 animate-pulse" />
                  <div className="p-6 sm:p-10 space-y-4">
                    <div className="h-8 w-2/3 bg-dark-100 rounded animate-pulse" />
                    <div className="h-5 w-1/3 bg-dark-100 rounded animate-pulse" />
                    <div className="h-24 bg-dark-100 rounded animate-pulse" />
                  </div>
                </div>
              </div>
              <aside className="space-y-6">
                <div className="bg-white rounded-3xl border border-dark-200 p-6 shadow-sm space-y-4">
                  <div className="h-16 bg-dark-100 rounded-2xl animate-pulse" />
                  <div className="h-14 bg-dark-100 rounded-2xl animate-pulse" />
                  <div className="h-14 bg-dark-100 rounded-2xl animate-pulse" />
                </div>
              </aside>
            </div>
          </main>
        </div>
      </>
    );
  }

  // ── Error / not found ─────────────────────────────────────
  if (error || !ad) {
    return (
      <>
        <Helmet>
          <title>Listing not found · BazaarPk</title>
        </Helmet>
        <div className="min-h-screen bg-surface">
          <Navbar mobileOpen={mobileOpen} onToggleMobile={toggleMobile} />
          <main className="max-w-4xl mx-auto px-4 sm:px-6 py-20 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-dark-100 rounded-full mb-6">
              {error ? (
                <AlertCircle className="w-10 h-10 text-danger" />
              ) : (
                <Info className="w-10 h-10 text-dark-400" />
              )}
            </div>
            <h1 className="text-3xl font-bold text-dark-900">
              {error ? 'Unable to load listing' : 'Listing not found'}
            </h1>
            <p className="mt-3 text-dark-600 max-w-md mx-auto">
              {error
                ? error
                : 'The item you are looking for is unavailable or has been removed.'}
            </p>
            <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
              {error && (
                <button
                  onClick={() => void refetch()}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white border border-dark-200 text-dark-800 font-bold hover:bg-dark-50 transition-all"
                >
                  <RefreshCw className="w-4 h-4" />
                  Try again
                </button>
              )}
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-bold transition-all shadow-lg shadow-primary-500/25"
              >
                Back to Marketplace
              </button>
            </div>
          </main>
        </div>
      </>
    );
  }

  // ── Derived data ──────────────────────────────────────────
  const categoryName = getCategoryName(ad);
  const categorySegments = getCategorySegments(ad);
  const activeImage = gallery[activeImageIdx] ?? PLACEHOLDER_IMAGE;
  const isNegotiable = Boolean(ad.is_price_negotiable);
  const contactPhone =
    (ad.show_contact && (ad.contact_number || ad.seller_phone)) || null;
  const canShowWhatsApp = Boolean(contactPhone);
  const whatsappHref = contactPhone ? buildWhatsAppLink(contactPhone, ad.title) : '#';
  const postedAgo = formatRelativeTime(ad.created_at);

  const specs: Array<{ label: string; value: string; icon: typeof Gauge }> = [];
  if (ad.is_vehicle || ad.year || ad.mileage || ad.fuel_type || ad.transmission) {
    if (ad.year) specs.push({ label: 'Year', value: String(ad.year), icon: Calendar });
    if (ad.mileage !== undefined && ad.mileage !== null) {
      specs.push({ label: 'Mileage', value: `${formatNumber(ad.mileage)} km`, icon: Gauge });
    }
    if (ad.make) specs.push({ label: 'Make', value: titleCase(ad.make), icon: Info });
    if (ad.model) specs.push({ label: 'Model', value: titleCase(ad.model), icon: Info });
    if (ad.fuel_type) specs.push({ label: 'Fuel Type', value: titleCase(ad.fuel_type), icon: Fuel });
    if (ad.transmission) specs.push({ label: 'Transmission', value: titleCase(ad.transmission), icon: Settings });
    if (ad.condition) specs.push({ label: 'Condition', value: titleCase(ad.condition), icon: CheckCircle2 });
  } else {
    if (ad.condition) specs.push({ label: 'Condition', value: titleCase(ad.condition), icon: CheckCircle2 });
    specs.push({ label: 'Posted By', value: ad.seller_name || 'Seller', icon: User });
    specs.push({ label: 'Category', value: categoryName, icon: Info });
  }

  const sellerInitial = (ad.seller_name || 'S').trim().charAt(0).toUpperCase();

  const requireAuth = (): boolean => {
    if (isAuthenticated) return true;

    toast.info('Please sign in to contact the seller.', {
      id: 'contact-auth-required',
      description: 'Login is required to reveal the number, send email, or chat on WhatsApp.',
    });
    return false;
  };

  const handleProtectedAction = (
    event?: MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ): boolean => {
    if (requireAuth()) return true;
    event?.preventDefault();
    event?.stopPropagation();
    return false;
  };

  const handleRevealNumber = (
    event?: MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ): void => {
    if (!handleProtectedAction(event)) return;
    setPhoneRevealed(true);
  };

  const handleShare = async (): Promise<void> => {
    const shareData = {
      title: ad.title,
      text: `${ad.title} — ${formatPKR(ad.price)}`,
      url: typeof window !== 'undefined' ? window.location.href : '',
    };
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        await navigator.share(shareData);
      } else if (shareData.url && navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareData.url);
      }
    } catch {
      // user cancelled — no-op
    }
  };

  return (
    <>
      <Helmet>
        <title>{ad.title} · BazaarPk</title>
        <meta
          name="description"
          content={`${ad.title} available in ${ad.city}. ${formatPKR(ad.price)} on BazaarPk.`}
        />
      </Helmet>

      <div className="min-h-screen bg-surface">
        <Navbar mobileOpen={mobileOpen} onToggleMobile={toggleMobile} />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10 pb-28 lg:pb-10">
          {/* Breadcrumbs & Actions */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm font-medium flex-wrap">
              <Link to="/" className="text-dark-500 hover:text-primary-600 transition-colors">
                Home
              </Link>
              {categorySegments.length > 0 ? (
                categorySegments.map((seg, i) => (
                  <span key={`${seg.name}-${i}`} className="inline-flex items-center gap-2">
                    <ChevronRight className="w-4 h-4 text-dark-300" />
                    {seg.slug ? (
                      <Link
                        to={`/category/${seg.slug}`}
                        className={i === categorySegments.length - 1 ? 'text-primary-600 font-semibold' : 'text-dark-500 hover:text-primary-600'}
                      >
                        {seg.name}
                      </Link>
                    ) : (
                      <span className={i === categorySegments.length - 1 ? 'text-primary-600 font-semibold' : 'text-dark-500'}>
                        {seg.name}
                      </span>
                    )}
                  </span>
                ))
              ) : (
                <>
                  <ChevronRight className="w-4 h-4 text-dark-300" />
                  <span className="text-primary-600 font-semibold">{categoryName}</span>
                </>
              )}
            </nav>

            <div className="flex items-center gap-3">
              <button
                onClick={async () => {
                  if (!isAuthenticated) {
                    setAuthOpen(true);
                    return;
                  }
                  if (!ad?.id) return;
                  // Optimistic UI
                  setIsSaved((v) => !v);
                  try {
                    const res = await savedService.toggleSaved(ad.id);
                    setIsSaved(Boolean(res.saved));
                  } catch {
                    // revert on error
                    setIsSaved((v) => !v);
                  }
                }}
                aria-pressed={isSaved}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all font-semibold text-sm ${
                  isSaved
                    ? 'bg-danger/10 border-danger/20 text-danger'
                    : 'bg-white border-dark-200 text-dark-600 hover:border-danger/30 hover:text-danger'
                }`}
              >
                <Heart className={`w-4 h-4 ${isSaved ? 'fill-danger text-danger' : 'text-dark-400'}`} />
                {isSaved ? 'Saved' : 'Save'}
              </button>
              <button
                onClick={() => void handleShare()}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-dark-200 text-dark-600 hover:bg-dark-50 transition-all font-semibold text-sm"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>
          </div>

          <div className="grid lg:grid-cols-[1fr_380px] gap-8 items-start">
            {/* Left Column */}
            <div className="space-y-8">
              {/* Gallery */}
              <article className="bg-white rounded-3xl border border-dark-200 overflow-hidden shadow-sm">
                <div className="relative group">
                  <div className="h-[400px] sm:h-[540px] bg-dark-50">
                    <img
                      src={activeImage}
                      alt={ad.title}
                      className="w-full h-full object-cover transition-transform duration-700"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                      }}
                    />
                  </div>

                  {gallery.length > 1 && (
                    <>
                      <button
                        onClick={() =>
                          setActiveImageIdx((i) => (i === 0 ? gallery.length - 1 : i - 1))
                        }
                        aria-label="Previous image"
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-dark-900 shadow-lg flex items-center justify-center transition-all"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() =>
                          setActiveImageIdx((i) => (i === gallery.length - 1 ? 0 : i + 1))
                        }
                        aria-label="Next image"
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-dark-900 shadow-lg flex items-center justify-center transition-all"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </>
                  )}

                  <div className="absolute bottom-6 left-6 flex gap-2 flex-wrap">
                    <span className="px-3 py-1.5 rounded-lg bg-dark-900/60 backdrop-blur-md text-white text-xs font-bold">
                      {gallery.length > 0 ? `${activeImageIdx + 1} / ${gallery.length}` : '0 / 0'}{' '}
                      Photos
                    </span>
                    {ad.is_featured && (
                      <span className="px-3 py-1.5 rounded-lg bg-primary-600/90 backdrop-blur-md text-white text-xs font-bold flex items-center gap-1.5">
                        <ShieldCheck className="w-3.5 h-3.5" />
                        FEATURED
                      </span>
                    )}
                    {ad.is_urgent && (
                      <span className="px-3 py-1.5 rounded-lg bg-danger/90 backdrop-blur-md text-white text-xs font-bold">
                        URGENT
                      </span>
                    )}
                  </div>
                </div>

                {/* Thumbnails */}
                {gallery.length > 1 && (
                  <div className="px-6 sm:px-10 py-4 border-b border-dark-100">
                    <div className="flex gap-3 overflow-x-auto">
                      {gallery.map((src, i) => (
                        <button
                          key={`${src}-${i}`}
                          onClick={() => setActiveImageIdx(i)}
                          aria-label={`Show image ${i + 1}`}
                          aria-current={i === activeImageIdx}
                          className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${
                            i === activeImageIdx
                              ? 'border-primary-600 ring-2 ring-primary-200'
                              : 'border-dark-200 hover:border-primary-300'
                          }`}
                        >
                          <img
                            src={src}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                            }}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="p-6 sm:p-10">
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-6 pb-6 border-b border-dark-100">
                    <div className="min-w-0 flex-1">
                      <h1 className="text-2xl sm:text-4xl font-black text-dark-900 leading-tight mb-2 tracking-tight break-words">
                        {ad.title}
                      </h1>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-dark-500">
                        <span className="flex items-center gap-1.5">
                          <MapPin className="w-4 h-4 text-primary-500" />
                          {[ad.location, ad.city].filter(Boolean).join(', ')}
                        </span>
                        {postedAgo && (
                          <span className="flex items-center gap-1.5">
                            <Clock className="w-4 h-4 text-warning" />
                            {postedAgo}
                          </span>
                        )}
                        <span className="flex items-center gap-1.5">
                          <Eye className="w-4 h-4 text-dark-300" />
                          {formatNumber(ad.view_count)} views
                        </span>
                      </div>
                    </div>
                    <div className="text-left sm:text-right">
                      <p className="text-3xl sm:text-4xl font-black text-primary-600 tracking-tight">
                        {formatPKR(ad.price)}
                      </p>
                      {isNegotiable && (
                        <span className="text-xs font-bold text-success uppercase tracking-widest mt-1 block">
                          Negotiable
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Specs grid */}
                  {specs.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
                      {specs.map((spec, i) => (
                        <div
                          key={`${spec.label}-${i}`}
                          className="bg-dark-50/50 p-4 rounded-2xl border border-dark-100"
                        >
                          <spec.icon className="w-5 h-5 text-primary-500 mb-2" />
                          <p className="text-[10px] font-bold text-dark-400 uppercase tracking-wider">
                            {spec.label}
                          </p>
                          <p className="text-sm font-bold text-dark-800 break-words">
                            {spec.value}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Description */}
                  <div className="prose prose-dark max-w-none">
                    <h2 className="text-xl font-bold text-dark-900 mb-4">Description</h2>
                    <p className="text-dark-600 leading-relaxed text-lg whitespace-pre-line">
                      {ad.description || 'No description provided.'}
                    </p>
                  </div>

                  {/* Safety */}
                  <div className="mt-12 p-6 rounded-2xl bg-warning/5 border border-warning/20">
                    <h3 className="text-sm font-bold text-warning-dark flex items-center gap-2 mb-3">
                      <ShieldCheck className="w-4 h-4" />
                      Safety Tips
                    </h3>
                    <ul className="text-xs text-dark-600 space-y-2 list-disc list-inside">
                      <li>Meet in a safe, public location.</li>
                      <li>Inspect the item before paying.</li>
                      <li>Never pay in advance — only pay when you have the item.</li>
                      <li>Use our chat system for initial communication.</li>
                    </ul>
                  </div>
                </div>
              </article>
            </div>

            {/* Right Column */}
            <aside className="space-y-6 lg:sticky lg:top-24">
              <div className="bg-white rounded-3xl border border-dark-200 p-6 shadow-sm">
                <div className="flex items-center gap-4 mb-6">
                  {ad.seller_image && sellerImgOk ? (
                    <img
                      src={ad.seller_image}
                      alt={ad.seller_name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-primary-100"
                      referrerPolicy="no-referrer"
                      loading="lazy"
                      onError={() => {
                        setSellerImgOk(false);
                      }}
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white text-2xl font-black">
                      {sellerInitial}
                    </div>
                  )}
                  <div className="min-w-0">
                    <h3 className="text-lg font-black text-dark-900 truncate">
                      {ad.seller_name || 'Private Seller'}
                    </h3>
                    <p className="text-xs font-medium text-dark-500">Member Since {ad.seller_since ? new Date(ad.seller_since).toLocaleDateString('en-PK', { month: 'short', year: 'numeric' }) : 'Unknown'}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <ShieldCheck className="w-3.5 h-3.5 text-success" />
                      <span className="text-[10px] font-bold text-dark-500">Verified</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  {ad.show_contact && contactPhone ? (
                    isAuthenticated && phoneRevealed ? (
                      <a
                        href={`tel:${contactPhone}`}
                        onClick={(event) => {
                          handleProtectedAction(event);
                        }}
                        className="w-full flex items-center justify-center gap-3 bg-primary-600 hover:bg-primary-700 text-white rounded-2xl py-4 font-bold transition-all shadow-lg shadow-primary-500/25 active:scale-95"
                      >
                        <Phone className="w-5 h-5" />
                        {contactPhone}
                      </a>
                    ) : (
                      <button
                        onClick={handleRevealNumber}
                        className="w-full flex items-center justify-center gap-3 bg-primary-600 hover:bg-primary-700 text-white rounded-2xl py-4 font-bold transition-all shadow-lg shadow-primary-500/25 active:scale-95"
                      >
                        <Phone className="w-5 h-5" />
                        Reveal Number
                      </button>
                    )
                  ) : (
                    <button
                      disabled
                      className="w-full flex items-center justify-center gap-3 bg-dark-100 text-dark-400 rounded-2xl py-4 font-bold cursor-not-allowed"
                    >
                      <Phone className="w-5 h-5" />
                      Phone hidden
                    </button>
                  )}

                  {ad.seller_email ? (
                    <a
                      href={`mailto:${ad.seller_email}?subject=${encodeURIComponent(
                        `Interested in: ${ad.title}`
                      )}`}
                      onClick={(event) => {
                        handleProtectedAction(event);
                      }}
                      className="w-full flex items-center justify-center gap-3 bg-white border-2 border-primary-100 text-primary-600 hover:bg-primary-50 rounded-2xl py-4 font-bold transition-all active:scale-95"
                    >
                      <Mail className="w-5 h-5" />
                      Email Seller
                    </a>
                  ) : (
                    <button
                      onClick={() => {
                        handleProtectedAction();
                      }}
                      className="w-full flex items-center justify-center gap-3 bg-white border-2 border-primary-100 text-primary-600 hover:bg-primary-50 rounded-2xl py-4 font-bold transition-all active:scale-95"
                    >
                      <MessageCircle className="w-5 h-5" />
                      Send Message
                    </button>
                  )}

                  {canShowWhatsApp && (
                    <a
                      href={whatsappHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(event) => {
                        handleProtectedAction(event);
                      }}
                      className="w-full flex items-center justify-center gap-3 bg-success text-white hover:bg-success-dark rounded-2xl py-4 font-bold transition-all active:scale-95 shadow-lg shadow-success/20"
                    >
                      <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.246 2.248 3.484 5.232 3.484 8.412-.003 6.557-5.338 11.892-11.893 11.892-1.997-.001-3.951-.5-5.688-1.448l-6.309 1.656zm6.224-3.82c1.516.903 3.125 1.378 4.773 1.379 5.438 0 9.863-4.425 9.866-9.863.001-2.633-1.025-5.11-2.89-6.975s-4.342-2.891-6.974-2.891c-5.439 0-9.865 4.426-9.867 9.865-.001 1.735.454 3.426 1.316 4.921l-1.073 3.918 4.02-.105-1.121-.649zm9.324-12.89c-.218-.485-.448-.494-.656-.503-.17-.008-.364-.008-.559-.008-.195 0-.513.073-.78.366-.267.293-1.021 1-1.021 2.439 0 1.439 1.047 2.829 1.192 3.025.145.195 2.06 3.146 4.991 4.414.697.302 1.241.483 1.666.618.701.223 1.34.191 1.845.116.563-.083 1.731-.707 1.974-1.39.243-.683.243-1.269.17-1.39-.073-.122-.267-.195-.559-.341-.293-.146-1.731-.854-1.998-.951-.267-.097-.462-.146-.656.146-.195.293-.755.951-.925 1.146-.17.195-.341.219-.633.073-.293-.146-1.235-.455-2.353-1.453-.87-.775-1.457-1.733-1.627-2.025-.17-.293-.018-.452.129-.597.132-.131.293-.341.44-.512.146-.171.195-.293.293-.488.097-.195.048-.366-.024-.512-.072-.146-.633-1.512-.867-2.036z" />
                      </svg>
                      Chat on WhatsApp
                    </a>
                  )}
                </div>
              </div>

              <div className="bg-dark-900 rounded-3xl p-6 text-white overflow-hidden relative group cursor-pointer">
                <div className="relative z-10">
                  <h4 className="font-black text-lg mb-2">Stay Safe</h4>
                  <p className="text-dark-300 text-xs leading-relaxed mb-4">
                    Follow our safety tips to ensure a secure transaction.
                  </p>
                  <button className="text-xs font-bold flex items-center gap-1.5 text-primary-400 group-hover:gap-2.5 transition-all">
                    Learn more <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
                <ShieldCheck className="absolute -bottom-4 -right-4 w-24 h-24 text-white/5 rotate-12" />
              </div>

              <button className="w-full py-4 flex items-center justify-center gap-2 text-dark-400 hover:text-danger font-bold text-sm transition-colors">
                <Flag className="w-4 h-4" />
                Report this listing
              </button>
            </aside>
          </div>
        </main>

        {/* Auth modal for save action when not logged in */}
        {authOpen && (
          <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} initialView="signin" />
        )}

        <Footer onOpenPrivacySettings={() => {}} />

        {/* Mobile sticky contact bar */}
        <div className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t border-dark-100 p-4 z-50 flex gap-3 shadow-[0_-10px_20px_rgba(0,0,0,0.05)]">
          {ad.show_contact && contactPhone ? (
            isAuthenticated && phoneRevealed ? (
              <a
                href={`tel:${contactPhone}`}
                onClick={(event) => {
                  handleProtectedAction(event);
                }}
                className="flex-1 flex items-center justify-center gap-2 bg-primary-600 text-white rounded-2xl py-4 font-bold active:scale-95 transition-all"
              >
                <Phone className="w-5 h-5" />
                Call
              </a>
            ) : (
              <button
                onClick={handleRevealNumber}
                className="flex-1 flex items-center justify-center gap-2 bg-primary-600 text-white rounded-2xl py-4 font-bold active:scale-95 transition-all"
              >
                <Phone className="w-5 h-5" />
                Reveal Number
              </button>
            )
          ) : (
            <button
              disabled
              className="flex-1 flex items-center justify-center gap-2 bg-dark-100 text-dark-400 rounded-2xl py-4 font-bold cursor-not-allowed"
            >
              <Phone className="w-5 h-5" />
              Call
            </button>
          )}
          {canShowWhatsApp ? (
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => {
                handleProtectedAction(event);
              }}
              className="flex-1 flex items-center justify-center gap-2 bg-success text-white rounded-2xl py-4 font-bold active:scale-95 transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Chat
            </a>
          ) : (
            <button
              onClick={() => {
                handleProtectedAction();
              }}
              className="flex-1 flex items-center justify-center gap-2 bg-success text-white rounded-2xl py-4 font-bold active:scale-95 transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Chat
            </button>
          )}
        </div>
      </div>
    </>
  );
}
